import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import type { MeResponse } from '@shared/types';
import { api, ApiError, serverNow } from '@/lib/api';
import { clearSession, loadSession, saveSession, type Session } from '@/lib/session';
import { isSeatMoved } from '@/lib/seat';
import { useProof } from '@/lib/useProof';
import { usePresence } from '@/lib/presence';
import { connectBattle } from '@/lib/realtime';
import { sfx } from '@/audio/sfx';
import { boardAt, selectPhase, selectRevealView, useGame } from '@/store/game';
import { Pulse } from '@/brand/Pulse';
import { PROOF } from '@/components/ProofMark';
import { OfflineBanner } from '@/components/OfflineBanner';
import Lobby from './Lobby';
import Question from './Question';
import Reveal from './Reveal';
import Leaderboard from './Leaderboard';
import Podium from './Podium';
import { Toast } from './parts/Toast';
import { Screen } from './parts/Screen';
import { useLateTick } from './parts/lateTick';
import { useRerenderAt, useCalm } from './parts/time';
import { COPY } from './parts/copy';
import { ME_ATTEMPTS, ME_FRESH_MS, ME_JITTER_MS, ME_RETRY_MS, type ResumeProblem, type ResumeState } from './parts/moments';
import { useBattleLook } from '@/look';
import { OpenInBrowser, useBrowserHint } from './parts/OpenInBrowser';

/** The version of the snapshot this phone holds for its own battle, or undefined (another battle's, or none yet). */
function heldVersion(): number | undefined {
  const g = useGame.getState(); const snap = g.snapshot; const code = g.session?.code;
  return snap && code && snap.code.toUpperCase() === code.toUpperCase() ? snap.version : undefined;
}
/** GET /me with ?v=: the server never answers from a cached battle older than the one this phone already shows. */
export function mePath(version = heldVersion()): string {
  return version !== undefined && Number.isInteger(version) && version > 0 ? `/me?v=${version}` : '/me';
}
/** This player's game on another device: the address the game itself keeps (token, code, player, name), for the computer's QR. */
function carryOnUrl(): string {
  const s = useGame.getState().session;
  if (!s) return window.location.href;
  const q = new URLSearchParams({ t: s.token, c: s.code, p: s.playerId, n: s.name });
  return `${window.location.origin}/play?${q.toString()}`;
}

/** The game: one page that follows the battle's state from the lobby to the podium, phase by phase. */
export default function Play() {
  const nav = useNavigate();
  const reduce = useCalm();
  const session = useGame((s) => s.session); const snapshot = useGame((s) => s.snapshot);
  const revealSeen = useGame((s) => s.revealSeen); const joined = useGame((s) => s.joined); const boardShownFor = useGame((s) => s.boardShownFor);
  const phase = selectPhase({ session, snapshot });
  // v2.2: the battle's look (clinical unless the battle says playful, and a locked look wins over any preview). The look
  // system also follows the game store by itself; reporting the snapshot here makes this page's battle explicit.
  useBattleLook(snapshot);
  // 9 Oct: the heartbeat (lib/presence), so the host and ops rosters can tell a phone that has gone from a player sitting
  // a question out. A confirmed answer counts as a sign of life, so the next beat can wait; a beat refused because the
  // seat moved to another device is acted on below, like the same refusal from any other call.
  usePresence({
    token: session?.token, code: session?.code, status: snapshot?.status,
    lastActivity: () => useGame.getState().lastAnswerAt ?? 0, onMoved: () => useGame.getState().markSeatMoved(),
  });

  // The session no longer works for this seat. Back to this battle's join link, prefilled, with a plain explanation
  // (the session ran out, the seat was not there, or another device took it); never an empty form.
  const leave = (problem: ResumeProblem, s: Session) => {
    clearSession(); const g = useGame.getState(); g.setSession(undefined); g.clearSeatMoved();
    nav(`/j/${s.code}`, { replace: true, state: { resume: problem, name: s.name } satisfies ResumeState });
  };
  // 9 Oct: the seat moved (lib/seat), seen by an answer, the heartbeat, the proof or a read of /me: one exit for all of them.
  const seatMoved = useGame((s) => s.seatMoved);
  useEffect(() => {
    if (!seatMoved) return;
    const s = useGame.getState().session ?? loadSession();
    if (s) leave('moved', s); else { useGame.getState().clearSeatMoved(); nav('/join', { replace: true }); }
  }, [seatMoved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect, then read /me: the server is the only source of truth for score, rank and answers.
  useEffect(() => {
    const g = useGame.getState();
    const s = g.session ?? loadSession();
    // 30 Oct 2026: the join form moved from / (now the front page) to /join.
    if (!s) { nav('/join', { replace: true }); return; }
    if (!g.session) g.setSession(s);
    const rt = connectBattle(s.code, { onSnapshot: (x) => useGame.getState().applySnapshot(x), onStatus: (st) => useGame.getState().setConnection(st), version: heldVersion });
    let stopped = false; let retry: number | undefined; let attempt = 0;
    const readMe = () => {
      api<MeResponse>(mePath(), { token: s.token }).then((me) => {
        if (stopped) return;
        const gg = useGame.getState(); gg.setMe(me); gg.applySnapshot(me.state);
        // A rejoin keeps the name the player first joined this battle with; show that one.
        const cur = gg.session ?? s;
        if (me.name && me.name !== cur.name) { const next = { ...cur, name: me.name }; saveSession(next); gg.setSession(next); }
      }).catch((e) => {
        if (stopped) return;
        if (e instanceof ApiError && (e.status === 401 || e.status === 403 || e.status === 404)) {
          leave(isSeatMoved(e) ? 'moved' : e.status === 404 ? 'missing' : 'expired', useGame.getState().session ?? s);
          return;
        }
        // Network trouble: stay in the game (the realtime channel and polling carry on) and try again shortly.
        retry = window.setTimeout(readMe, Math.min(15_000, 2000 * 2 ** attempt++));
      });
    };
    const fresh = g.me && g.me.playerId === s.playerId && g.meAt !== undefined && Date.now() - g.meAt < ME_FRESH_MS;
    if (!fresh) readMe();
    return () => { stopped = true; rt.close(); if (retry) window.clearTimeout(retry); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh my score and rank whenever the room moves to a reveal, the podium or the end, and again when the question on
  // screen is voided (my score loses its points). The points on the answer view and my line on the leaderboard wait for
  // this read, so a failure is tried again a couple of times.
  const voidedNow = snapshot?.status === 'reveal' && snapshot.reveal?.index === snapshot.index && Boolean(snapshot.reveal.voided);
  useEffect(() => {
    const token = useGame.getState().session?.token;
    if (!token || !['reveal', 'podium', 'ended'].includes(phase)) return;
    let stopped = false; let attempt = 0; let t: number | undefined;
    const read = () => {
      api<MeResponse>(mePath(), { token })
        .then((me) => { if (!stopped && me.playerId === useGame.getState().session?.playerId) useGame.getState().setMe(me); })
        .catch((e) => {
          if (stopped) return;
          if (isSeatMoved(e)) { useGame.getState().markSeatMoved(); return; }
          if (++attempt < ME_ATTEMPTS) t = window.setTimeout(read, ME_RETRY_MS * attempt);
        });
    };
    t = window.setTimeout(read, Math.random() * ME_JITTER_MS);
    return () => { stopped = true; if (t) window.clearTimeout(t); };
  }, [phase, snapshot?.index, voidedNow]);

  // 9 Oct: proof of identity (lib/useProof). Back from sign-in, or already signed in on this device, with a session that
  // can prove the joined address, while the server still offers proof: prove it once, read /me again for the details and
  // the record, and say so. A session for another address does nothing, and the icon stays.
  const me = useGame((s) => s.me);
  useProof({ session, me, mePath, onProved: (m) => { const g = useGame.getState(); g.setMe(m); g.showToast(PROOF.done); } });

  useLateTick();
  // Inside Instagram, the "open in your browser" hint; on a computer, a QR to carry on on a phone. Once per device, and
  // only in the lobby, never over a question. The join page usually showed it already.
  const hint = useBrowserHint(phase === 'lobby');

  // Sound is off until the first tap (the join tap usually did it already).
  useEffect(() => {
    const unlock = () => sfx.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  // The answer view gives way to the leaderboard at a server moment.
  const seenAt = snapshot && revealSeen?.index === snapshot.index ? revealSeen.at : undefined;
  useRerenderAt(snapshot ? boardAt(snapshot, seenAt) : undefined);
  const view = selectRevealView({ snapshot, revealSeen, joined, boardShownFor }, serverNow());

  let key: string; let screen: ReactNode;
  const waiting = (text: string) => (
    <Screen center>
      <div className="flex flex-col items-center gap-3 text-center text-brand">
        <Pulse mood="steady" width={160} height={40} />
        <p className="font-semibold text-ink-2">{text}</p>
      </div>
    </Screen>
  );
  if (phase === 'join' || !snapshot) { key = 'loading'; screen = waiting(COPY.join.checking); }
  else if (phase === 'lobby') { key = 'lobby'; screen = <Lobby />; }
  else if (phase === 'preroll' || phase === 'question') {
    // A question snapshot carries its question, or with a stream delay its stub (`upcoming`) until the text arrives, which
    // the question screen shows as the intro card (17 Oct 2026); if one ever arrives with neither, wait for the next.
    const q = snapshot.question ?? snapshot.upcoming;
    key = `q${snapshot.index}`; screen = q ? <Question /> : waiting(COPY.board.nextSoon);
  }
  else if (phase === 'reveal') { key = `${view}${snapshot.index}`; screen = view === 'board' ? <Leaderboard /> : <Reveal />; }
  else { key = 'end'; screen = <Podium />; }

  return (
    // Motion follows the viewer's reduced-motion setting for every animation on these screens.
    <MotionConfig reducedMotion="user">
      <div className="relative min-h-full overflow-x-clip">
        {/* Once for the whole game, in flow above the screen and its header band, so a screen on its way out never shows a second one. */}
        <OfflineBanner />
        <AnimatePresence mode="popLayout">
          <motion.div key={key} className="min-h-full"
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} exit={reduce ? { opacity: 0 } : { opacity: 0, x: -28 }}
            transition={{ type: 'spring', stiffness: 360, damping: 34, opacity: { duration: 0.16 } }}>
            {screen}
          </motion.div>
        </AnimatePresence>
        <Toast />
        {hint.kind && (
          <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <OpenInBrowser kind={hint.kind} os={hint.os} url={carryOnUrl()} where="play" onClose={hint.dismiss} className="w-full max-w-md shadow-pop" />
          </div>
        )}
      </div>
    </MotionConfig>
  );
}
